import { useState, type FormEvent } from 'react'
import { AleloButton, AleloInput, AleloLayout } from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert, RangoLink } from '@/components/rango'
import { AleloCaptcha } from '@/components/AleloCaptcha'
import { formatCpf, isValidCpfLength } from '@/utils/cpf'
import { executeSafely } from '@/utils/helpers'
import { requestPasswordResetSms } from '@/utils/passwordResetApi'
import {
  useResetPasswordRequestManager,
  type ResetChannel,
} from './hooks/useResetPasswordRequestManager'
import locales from './locales/pt-BR.json'

/**
 * S7 — Esqueci minha senha (reset-password-request).
 * CPF + "Receber por e-mail" / "Receber por SMS" + confirmação de envio.
 * E-mail: reset nativo da Auth0. SMS: o BFF manda um link de troca de senha
 * (ticket) ao celular verificado; a resposta é sempre neutra.
 */
export default function ResetPasswordRequestScreen() {
  const {
    handleContinue,
    handleBack,
    error: sdkError,
    captchaConfig,
    captchaReady,
    onCaptchaToken,
  } = useResetPasswordRequestManager()
  const [cpf, setCpf] = useState('')
  const [error, setError] = useState<string>()
  const [sent, setSent] = useState<ResetChannel | null>(null)
  const [pending, setPending] = useState<ResetChannel | null>(null)

  async function submitChannel(channel: ResetChannel) {
    setError(undefined)
    setSent(null)
    if (!isValidCpfLength(cpf)) {
      setError(locales.invalidCpf)
      return
    }
    setPending(channel)
    if (channel === 'sms') {
      const result = await requestPasswordResetSms(cpf)
      setPending(null)
      if (result === 'ok') setSent('sms')
      else setError(locales.smsFailed)
      return
    }
    try {
      await executeSafely(() => handleContinue(cpf))
      setSent(channel)
    } catch (err) {
      if (err instanceof Error && err.message === 'CPF_INVALID') {
        setError(locales.invalidCpf)
      } else if (err instanceof Error && err.message === 'CAPTCHA_REQUIRED') {
        setError(locales.captchaRequired)
      } else {
        setError(locales.resetFailed)
      }
    } finally {
      setPending(null)
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    void submitChannel('email')
  }

  const busy = pending !== null
  // Erro de CPF fica no campo; os demais no bloco único do formulário.
  const fieldError = error === locales.invalidCpf ? error : undefined
  const formError = error && error !== locales.invalidCpf ? error : sdkError

  return (
    <AleloLayout>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{locales.description}</p>

      {sent ? (
        <RangoAlert
          tone="positive"
          title="Instruções enviadas"
          className={styles.feedback}
        >
          {sent === 'sms' ? locales.sentSms : locales.sentEmail}
        </RangoAlert>
      ) : null}

      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        <AleloInput
          id="cpf"
          name="username"
          required
          label={locales.cpfLabel}
          supportText={locales.cpfSupport}
          inputMode="numeric"
          autoComplete="username"
          placeholder={locales.cpfPlaceholder}
          value={cpf}
          error={fieldError}
          onChange={(e) => setCpf(formatCpf(e.target.value))}
          maxLength={14}
          disabled={busy}
        />
        <AleloCaptcha config={captchaConfig} onToken={onCaptchaToken} />

        {formError ? <RangoAlert tone="negative">{formError}</RangoAlert> : null}

        <div className={styles.actions}>
          <AleloButton
            type="submit"
            disabled={busy || !captchaReady}
            loading={pending === 'email'}
          >
            {pending === 'email' ? locales.sendingEmail : locales.submitEmail}
          </AleloButton>
          <AleloButton
            type="button"
            variant="secondary"
            disabled={busy || !captchaReady}
            loading={pending === 'sms'}
            onClick={() => void submitChannel('sms')}
          >
            {pending === 'sms' ? locales.sendingSms : locales.submitSms}
          </AleloButton>
        </div>

        <div className={styles.center}>
          <RangoLink onClick={() => void handleBack()}>
            {locales.back}
          </RangoLink>
        </div>
      </form>
    </AleloLayout>
  )
}
